import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardProps } from '../types';
import { SidebarNew } from './SidebarNew';
import StudyPlanWidget from './onboarding/StudyPlanWidget';
import ProgressCards from './ProgressCards';
import FlashcardModule from './FlashcardModule';
import DigitalTextbookModule from './DigitalTextbookModule';
import AudiobooksSection from './AudiobooksSection';
import TestyModule from './TestyModule';
import UserSettingsPage from './UserSettingsPage';
import UpgradePage from './UpgradePage';
import UpgradeSuccessPage from './UpgradeSuccessPage';
import HelpPage from './HelpPage';
import FeedbackPage from './FeedbackPage';
import StreakNotificationManager from './StreakNotificationManager';
import NewsSection from './NewsSection';
import TutorModule from './TutorModule';
import OspDashboard from './OspDashboard';
import QuizRecapModule from './QuizRecapModule';
import { AdminStreakControls } from './AdminStreakControls';
import { isFeatureEnabled, FEATURE_FLAGS } from '../config/featureFlags';
import { getStudyState, saveStudyState } from '../services/studyPlanStore';
import { useAuth } from '../AuthContext';
import { getTransferablePreAuthData, clearPreAuthWizardData } from '../services/preAuthWizardCache';
import { listChats, deleteChat, ChatSummary } from '../services/ospFirebaseService';

const Dashboard: React.FC<DashboardProps> = ({ user, progressData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Derive state from URL
  const pathname = location.pathname;
  const isOspMode = pathname === '/osp';

  // Determine active section from path
  const getSectionFromPath = (path: string) => {
    if (path === '/osp') return 'home'; // Tutor is home for OSP
    if (path === '/home' || path === '/') return 'home';
    // Handle special cases for upgrade success, settings, help, etc.
    if (path === '/upgrade-success') return 'upgrade-success';
    if (path === '/upgrade') return 'upgrade';
    if (path === '/settings') return 'settings';
    if (path === '/help') return 'help';
    if (path === '/feedback') return 'feedback';
    if (path === '/quiz-recap') return 'quiz-recap';
    if (path === '/textbook') return 'courses'; // Alias for courses/textbook module
    return path.substring(1); // e.g. /courses -> courses
  };

  const activeSection = getSectionFromPath(pathname);
  const dashboardMode = isOspMode ? 'OSP' : 'ZSV';

  const [hasCheckedPlan, setHasCheckedPlan] = useState(false);
  const [selectedFlashcardSet, setSelectedFlashcardSet] = useState<string | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [autoPlayChapter, setAutoPlayChapter] = useState<string | null>(null);
  const [resetTextbook, setResetTextbook] = useState(false);
  const [resetFlashcards, setResetFlashcards] = useState(false);
  const [isQuizRecap, setIsQuizRecap] = useState(false);

  // Welcome Animation State
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false);

  // Multi-chat state for OSP
  const [ospChatList, setOspChatList] = useState<ChatSummary[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [requestedChatId, setRequestedChatId] = useState<string | null>(null);
  const [forceNewChat, setForceNewChat] = useState(false);
  const [autoprompt, setAutoprompt] = useState<string | null>(null);

  // OSP dashboard vs chat view state
  const [ospView, setOspView] = useState<'dashboard' | 'chat'>('dashboard');
  const prevPathRef = useRef(pathname);

  // Handle OSP routing - show dashboard or chat based on params
  useEffect(() => {
    if (pathname === '/osp') {
      const urlParams = new URLSearchParams(location.search);
      const prompt = urlParams.get('prompt');
      const mode = urlParams.get('mode');

      // If opening /osp natively without params
      if (!prompt && !mode) {
        if (prevPathRef.current !== '/osp') {
          setOspView('dashboard');
        }
      } else {
        let missionPrompt = prompt;

        if (!missionPrompt && mode === 'mission') {
          try {
            const pendingMissionRaw = localStorage.getItem('pendingMission');
            if (pendingMissionRaw) {
              const missionData = JSON.parse(pendingMissionRaw);
              if (missionData && missionData.label) {
                missionPrompt = `Procvič se mnou: ${missionData.label}`;
              }
            }
          } catch (e) {
            console.error("Failed to parse pendingMission in Dashboard", e);
          }
        }

        if (missionPrompt) {
          setOspView('chat');
          setAutoprompt(missionPrompt);
          setForceNewChat(false); // autoprompt effect in TutorModule creates its own chat
        }
      }
    }
    prevPathRef.current = pathname;
  }, [pathname, location.search]);

  useEffect(() => {
    const justFinished = sessionStorage.getItem('justFinishedOnboarding');
    if (location.state?.onboardingCompleted || justFinished) {
      setShowWelcomeAnimation(true);
      // Clear flags
      window.history.replaceState({}, document.title);
      sessionStorage.removeItem('justFinishedOnboarding');

      // Auto-hide animation after some time (optional, but good for cleanup)
      // keeping it true for now so widget text stays "Vítej!"
    }
  }, [location]);

  // Check if user has a study plan, if not, redirect to onboarding (only once)
  // Only active when ONBOARDING_WIZARD feature flag is enabled
  // Skip redirect if user has explicitly skipped onboarding
  // Handles pre-auth wizard data transfer for new users
  useEffect(() => {
    const checkAndRedirect = async () => {
      if (FEATURE_FLAGS.ONBOARDING_WIZARD && currentUser && !hasCheckedPlan && (pathname === '/' || pathname === '/home')) {
        // Check if user has skipped onboarding
        const hasSkipped = localStorage.getItem('onboardingSkipped') === 'true';
        if (hasSkipped) {
          setHasCheckedPlan(true);
          return;
        }

        try {
          const state = await getStudyState(currentUser.uid);

          // Check if there's pre-auth wizard data to transfer (new user who completed wizard before signup)
          const preAuthData = getTransferablePreAuthData();

          if (preAuthData && preAuthData.studyPlan) {
            // New user completed wizard before creating account - transfer the data
            console.log("[Dashboard] Found pre-auth wizard data, transferring to user account");
            await saveStudyState(currentUser.uid, {
              onboardingData: preAuthData.onboardingData as any,
              studyPlan: preAuthData.studyPlan,
              version: 1,
            });
            clearPreAuthWizardData();
            setHasCheckedPlan(true);
            // Show welcome animation since they just completed onboarding
            sessionStorage.setItem('justFinishedOnboarding', 'true');
            setShowWelcomeAnimation(true);
            return;
          }

          if (!state || !state.studyPlan) {
            // User doesn't have a plan and didn't complete pre-auth wizard, redirect to onboarding
            console.log("[Dashboard] No study plan found, redirecting to onboarding");
            navigate('/onboarding');
          }
        } catch (e) {
          console.error("[Dashboard] Error checking study plan:", e);
        }
        setHasCheckedPlan(true);
      }
    };

    checkAndRedirect();
  }, [currentUser, hasCheckedPlan, pathname, navigate]);

  // Fetch OSP chat list when in OSP mode
  const refreshChatList = useCallback(() => {
    if (currentUser) {
      listChats(currentUser.uid).then(setOspChatList);
    }
  }, [currentUser]);

  useEffect(() => {
    if (dashboardMode === 'OSP' && currentUser) {
      refreshChatList();
    }
  }, [dashboardMode, currentUser, refreshChatList]);

  const handleDeleteChat = useCallback(async (chatId: string) => {
    if (!currentUser) return;
    await deleteChat(currentUser.uid, chatId);
    if (chatId === activeChatId) {
      setActiveChatId(null);
      setForceNewChat(true);
    }
    refreshChatList();
  }, [currentUser, activeChatId, refreshChatList]);

  // Handle URL-based params (for deep linking content)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    if (urlParams.get('success') === 'true' && urlParams.get('session_id')) {
      // Ensure we are on the upgrade success page if params are present
      if (activeSection !== 'upgrade-success') navigate('/upgrade-success' + location.search);
    } else if (pathname === '/textbook' || pathname === '/courses') {
      const chapter = urlParams.get('chapter');
      // Redirect old quiz-only URLs
      const mode = urlParams.get('mode');
      if (mode === 'quiz' && chapter) {
        console.log('[Dashboard] Redirecting old quiz URL to /quiz-recap');
        navigate(`/quiz-recap?chapter=${chapter}`, { replace: true });
        return;
      }
      if (chapter) setSelectedChapterId(chapter);
    } else if (pathname === '/flashcards') {
      const setParam = urlParams.get('set') || urlParams.get('topic');
      if (setParam) setSelectedFlashcardSet(setParam);
    } else if (pathname === '/quiz-recap') {
      setIsQuizRecap(true);
      const chapter = urlParams.get('chapter');
      if (chapter) setSelectedChapterId(chapter);
    } else {
      setIsQuizRecap(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, dashboardMode, activeSection, navigate]);

  const handleNavigation = useCallback((section: string, flashcardSetId?: string, audioChapterId?: string) => {
    let targetPath = '';

    // Handle special sections
    if (section === 'home') {
      // If we are currently in OSP mode, 'home' means stay in OSP (Tutor). 
      // If we are in ZSV mode, 'home' means /home.
      // However, if the sidebar explicitly wants to switch modes, it should navigate directly.
      // But the SidebarNew calls onNavigate('home') after setting mode... 
      // Wait, SidebarNew calls navigate('/osp') directly so onNavigate('home') is only for ZSV home.
      targetPath = '/home';
    } else if (section === 'tutor' || section === 'osp') {
      targetPath = '/osp';
    } else {
      targetPath = `/${section}`;
    }

    // Preservation of query params if needed can be handled here, but for now we clean slate mostly
    let searchParams = new URLSearchParams();

    if (section === 'flashcards' && flashcardSetId) {
      searchParams.set('set', flashcardSetId);
    }

    // Audio chapter persistence
    if (section === 'audiobook' && audioChapterId) {
      setAutoPlayChapter(audioChapterId);
    } else if (section !== 'audiobook') {
      setAutoPlayChapter(null);
    }

    // Resets for component state when navigating to same section
    if (section === 'courses' && activeSection === 'courses') {
      setResetTextbook(true);
      setTimeout(() => setResetTextbook(false), 100);
    }
    if (section === 'flashcards' && activeSection === 'flashcards') {
      setResetFlashcards(true);
      setTimeout(() => setResetFlashcards(false), 100);
    }

    // Navigate
    const queryString = searchParams.toString();
    navigate(`${targetPath}${queryString ? '?' + queryString : ''}`);

    console.log(`Navigating to: ${targetPath}`);
  }, [activeSection, navigate]);

  // Handle tour navigation events
  useEffect(() => {
    const handleTourNavigation = (event: CustomEvent) => {
      const { section } = event.detail;
      console.log(`Dashboard: Received tour navigation request for: ${section}`);

      // Handle special tour actions
      if (section === 'open-sidebar') {
        // SidebarNew handles its own state, might need a ref or context if we want to control it externally
        // For now, we'll skip this as the new sidebar is hover-based/toggle-based
        return;
      }

      // Map tour sections to dashboard sections
      const sectionMap: { [key: string]: string } = {
        'flashcards': 'flashcards',
        'flashcard-review': 'flashcards',
        'courses': 'courses',
        'audiobooks': 'audiobook',
        'home': 'home',
        'settings': 'settings',
        'help': 'help'
      };

      const targetSection = sectionMap[section] || section;
      if (targetSection && targetSection !== activeSection) {
        handleNavigation(targetSection);
      }
    };

    // Add event listener for tour navigation
    document.addEventListener('tour-navigation', handleTourNavigation as EventListener);

    return () => {
      document.removeEventListener('tour-navigation', handleTourNavigation as EventListener);
    };
  }, [activeSection, handleNavigation]);

  return (
    <div className="fixed inset-0 flex flex-col md:flex-row w-full bg-background dark:bg-dark-bg transition-colors duration-200 overflow-hidden" data-dashboard>
      {/* New Aceternity Sidebar */}
      <SidebarNew
        user={user}
        activeSection={activeSection}
        onNavigate={handleNavigation}
        dashboardMode={dashboardMode}
        setDashboardMode={(mode) => {
          if (mode === 'OSP') navigate('/osp');
          else navigate('/home');
        }}
        ospChatList={ospChatList}
        activeChatId={activeChatId}
        onSelectChat={(chatId) => {
          setRequestedChatId(chatId);
          setOspView('chat');
        }}
        onNewChat={() => {
          setForceNewChat(true);
          setOspView('chat');
        }}
        onDeleteChat={handleDeleteChat}
        onAutoPrompt={(prompt) => {
          setForceNewChat(false); // autoprompt creates its own new chat
          setAutoprompt(prompt);
          setOspView('chat');
        }}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Main Content */}
        <main className={`flex-1 flex flex-col overflow-x-hidden ${((dashboardMode === 'OSP' && ospView === 'chat') || isQuizRecap) ? 'overflow-hidden p-0' : 'overflow-y-auto p-4 sm:p-6 lg:p-8'}`}>
          <div className={`h-full flex flex-col w-full min-w-0 ${((dashboardMode === 'OSP' && ospView === 'chat') || isQuizRecap || activeSection === 'flashcards') ? '' : 'max-w-7xl mx-auto'}`}>
            {/* Quiz Recap Mode - Full Screen Overlay */}
            {isQuizRecap && (
              <>
                {console.log('[Dashboard] Rendering QuizRecapModule')}
                <div className="h-full w-full bg-gray-50 dark:bg-gray-900">
                  <QuizRecapModule />
                </div>
              </>
            )}

            {/* Global Sections (accessible from both modes) */}
            {!isQuizRecap && activeSection === 'settings' && (
              <UserSettingsPage user={user} onNavigate={handleNavigation} />
            )}

            {!isQuizRecap && activeSection === 'upgrade' && (
              <UpgradePage onNavigate={handleNavigation} />
            )}

            {!isQuizRecap && activeSection === 'upgrade-success' && (
              <UpgradeSuccessPage onNavigate={handleNavigation} />
            )}

            {!isQuizRecap && activeSection === 'help' && (
              <HelpPage onNavigate={handleNavigation} />
            )}

            {!isQuizRecap && activeSection === 'feedback' && (
              <FeedbackPage />
            )}

            {/* Mode Specific Content */}
            {!isQuizRecap && !['settings', 'upgrade', 'upgrade-success', 'help', 'feedback'].includes(activeSection) && (
              dashboardMode === 'OSP' ? (
                ospView === 'chat' ? (
                  <TutorModule
                    requestedChatId={requestedChatId}
                    forceNewChat={forceNewChat}
                    onForceNewChatHandled={() => setForceNewChat(false)}
                    onChatChanged={(chatId) => {
                      setActiveChatId(chatId);
                      setRequestedChatId(null);
                      refreshChatList();
                    }}
                    autoprompt={autoprompt}
                    onAutopromptHandled={() => setAutoprompt(null)}
                    onBackToDashboard={() => {
                      setAutoprompt(null);
                      setRequestedChatId(null);
                      setOspView('dashboard');
                      refreshChatList();
                    }}
                  />
                ) : (
                  <OspDashboard
                    ospChatList={ospChatList}
                    onOpenTutor={() => {
                      setForceNewChat(true);
                      setOspView('chat');
                    }}
                    onSelectChat={(chatId) => {
                      setRequestedChatId(chatId);
                      setOspView('chat');
                    }}
                    onDeleteChat={handleDeleteChat}
                    onAutoPrompt={(prompt) => {
                      setForceNewChat(false); // autoprompt creates its own new chat
                      setAutoprompt(prompt);
                      setOspView('chat');
                    }}
                  />
                )
              ) : (
                <>
                  {activeSection === 'home' && (
                    <>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                        {FEATURE_FLAGS.ONBOARDING_WIZARD ? (
                          <div className={isFeatureEnabled('OSP_TUTOR_MODULE', user.email) ? "lg:col-span-2 h-full" : "lg:col-span-3 h-full"}>
                            <StudyPlanWidget
                              userName={user.name}
                              onSwitchToOSP={() => {
                                navigate('/osp');
                              }}
                              showWelcome={showWelcomeAnimation}
                            />
                          </div>
                        ) : (
                          <div className="lg:col-span-3 h-full">
                            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                              <h2 className="text-2xl font-bold mb-2">Vítej zpět, {user.name}! 👋</h2>
                              <p className="text-gray-600 dark:text-gray-400">Začni svou sérii...</p>
                            </div>
                          </div>
                        )}
                        {isFeatureEnabled('OSP_TUTOR_MODULE', user.email) && (
                          <div className="lg:col-span-1 h-full">
                            <NewsSection onOpenOSPTutor={() => navigate('/osp')} />
                          </div>
                        )}
                      </div>
                      <ProgressCards progressData={progressData} onNavigate={handleNavigation} userId={user.id} />
                    </>
                  )}

                  {activeSection === 'flashcards' && (
                    <FlashcardModule preSelectedTopicId={selectedFlashcardSet} resetToTopics={resetFlashcards} onNavigate={handleNavigation} />
                  )}

                  {activeSection === 'courses' && (
                    <DigitalTextbookModule
                      onNavigate={handleNavigation}
                      resetToList={resetTextbook}
                      initialChapterId={selectedChapterId}
                    />
                  )}

                  {activeSection === 'audiobook' && (
                    <AudiobooksSection
                      autoPlayChapter={autoPlayChapter || undefined}
                      key={autoPlayChapter} // Force re-render when autoPlayChapter changes
                      onNavigate={handleNavigation}
                    />
                  )}

                  {activeSection === 'testy' && (
                    <TestyModule onNavigate={handleNavigation} />
                  )}

                  {activeSection !== 'home' && activeSection !== 'flashcards' && activeSection !== 'courses' && activeSection !== 'audiobook' && activeSection !== 'testy' && (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">🚧</div>
                      <h2 className="text-2xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
                        Sekce v přípravě
                      </h2>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins">
                        Tato sekce bude brzy dostupná.
                      </p>
                    </div>
                  )}
                </>
              )
            )}
          </div>
        </main>
      </div>

      {/* Streak Notification Manager - handles celebration and missed streak notifications */}
      <StreakNotificationManager />

      {/* Admin Streak Controls (only visible to admins) */}
      <AdminStreakControls />

    </div>
  );
};

export default Dashboard;